import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../../context/UserContext.js';
import MensajeCargando from '../../UI/MensajeCargando.js';
import MensajeError from '../../UI/MensajeError.js';
import MensajeGrabando from '../../UI/MensajeGrabando.js';
import TablaInstanciaFixture from './TablaInstanciaFixture.js';
import useGetListaURL from '../../../hooks/useGetListaURL.js';
import usePostURL from '../../../hooks/usePostURL.js';
import usePatchURL from '../../../hooks/usePatchURL.js';
import useEdicionFilaPartido from '../../../hooks/useEdicionFilaPartido.js';
import * as Instancia from '../../constantes/InstanciaPartido.js';
import * as Funciones from '../../funciones/FuncionesFixture.js';
import * as DTOs from '../../DTOs/PartidoFixtureDTOs.js';
import styles from './FixtureEtapaDefinicion.module.css';

const mapearPartidosConInstancia = (partidos) => {
  return (partidos || []).map((p) => ({
    ...p,
    codigoInstancia: Funciones.MapearInstanciaACodigo(p.instancia),
  }));
};

/**
 * Componente que gestiona la carga del fixture para una etapa de definición
 * Muestra partidos existentes y filas editables para crear los faltantes
 */
const FixtureEtapaDefinicion = (props) => {
  const datosElegidos = props.datosElegidos; // { torneo, categoria, etapa }
  const etapa = datosElegidos?.etapa;
  const torneo = datosElegidos?.torneo;
  const { usuario } = useContext(UserContext);
  const puedeEditar = usuario && usuario.puedeEditar;

  const [partidosExistentes, setPartidosExistentes] = useState([]);
  const [partidosFaltantes, setPartidosFaltantes] = useState([]);
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);

  const { isCargando, erroresGetLista, fetchData, resetErroresGetLista } = useGetListaURL();
  const { isGrabando, erroresPost, postData, resetErroresPost } = usePostURL();
  const { isGrabando: isGrabandoPatch, erroresPatch, patchData, resetErroresPatch } = usePatchURL();
  const edicion = useEdicionFilaPartido({ erroresPost, erroresPatch });
  const { partidosEnEdicion, partidoGrabandose, erroresFilaPartido } = edicion;

  const recargarPartidos = () => {
    const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
    fetchData(url, (partidos) => {
      setPartidosExistentes(mapearPartidosConInstancia(partidos));
    });
  };

  // Cargar partidos existentes de la etapa
  useEffect(() => {
    resetErroresGetLista();
    if (torneo && etapa && etapa.etapa) {
      const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
      fetchData(url, (partidos) => {
        setPartidosExistentes(mapearPartidosConInstancia(partidos));
      });
    } else {
      setPartidosExistentes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torneo, etapa, fetchData, resetErroresGetLista]);

  // Cargar equipos disponibles (participantes del torneo)
  useEffect(() => {
    if (torneo) {
      fetchData(`torneos/${torneo.id}/participantes`, (participantes) => {
        const equipos = participantes.map((p) => p.equipo).filter((e) => e != null);
        setEquiposDisponibles(equipos);
      });
    } else {
      setEquiposDisponibles([]);
    }
  }, [torneo, fetchData]);

  // Calcular partidos faltantes
  useEffect(() => {
    if (etapa && datosElegidos) {
      const faltantes = Funciones.PartidosFaltantesDefinicion(partidosExistentes, datosElegidos);
      setPartidosFaltantes(faltantes);
    } else {
      setPartidosFaltantes([]);
    }
  }, [partidosExistentes, etapa, datosElegidos]);

  const onPartidoChange = (idPartido, partidoPatch) => {
    const nuevosPartidos = [...partidosFaltantes];
    const index = nuevosPartidos.findIndex((p) => p.idPartido === idPartido);
    if (index === -1) {
      return;
    }

    const partidoActual = nuevosPartidos[index] || {};
    nuevosPartidos[index] = {
      ...partidoActual,
      ...partidoPatch,
    };

    // Si es ida y el partido tiene cantidadRuedas=2, replicar a vuelta
    if (etapa.cantidadRuedas === 2 && nuevosPartidos[index].codigoInstancia === Instancia.IDA) {
      const indexVuelta = nuevosPartidos.findIndex(
        (p) =>
          p.codigoInstancia === Instancia.VUELTA &&
          p.indiceCruce === nuevosPartidos[index].indiceCruce
      );

      if (indexVuelta !== -1) {
        let partidoVuelta = nuevosPartidos[indexVuelta];

        if (nuevosPartidos[index].equipoLocal) {
          partidoVuelta = Funciones.ReplicarEquipoIdaVuelta(
            nuevosPartidos[index],
            partidoVuelta,
            'equipoLocal'
          );
        }
        if (nuevosPartidos[index].equipoVisitante) {
          partidoVuelta = Funciones.ReplicarEquipoIdaVuelta(
            nuevosPartidos[index],
            partidoVuelta,
            'equipoVisitante'
          );
        }

        nuevosPartidos[indexVuelta] = partidoVuelta;
      }
    }

    setPartidosFaltantes(nuevosPartidos);
  };

  const onGuardarPartido = async (partido) => {
    const errorBase = Funciones.ValidarPartidoFixture(partido, { requiereFecha: true });
    if (errorBase) {
      alert(errorBase);
      return;
    }

    if (partido.codigoInstancia === Instancia.IDA || partido.codigoInstancia === Instancia.UNICO) {
      const otrosPartidosInstancia = partidosFaltantes.filter(
        (p) => p.idPartido !== partido.idPartido && p.codigoInstancia === partido.codigoInstancia
      );
      const partidosExistentesInstancia = partidosExistentes.filter(
        (p) => p.codigoInstancia === partido.codigoInstancia
      );
      const errorRepetidos = Funciones.ValidarEquiposRepetidos(
        [partido],
        [...otrosPartidosInstancia, ...partidosExistentesInstancia],
        'instancia'
      );
      if (errorRepetidos) {
        alert(errorRepetidos);
        return;
      }
    }

    resetErroresPost();
    edicion.limpiarErrorFila(partido.idPartido);
    edicion.setPartidoGrabandose(partido.idPartido);

    // Si es un partido de IDA (nuevo), SIEMPRE crear también el partido de VUELTA
    if (partido.codigoInstancia === Instancia.IDA && etapa.cantidadRuedas === 2) {
      let partidoVuelta = partidosFaltantes.find(
        (p) => p.codigoInstancia === Instancia.VUELTA && p.indiceCruce === partido.indiceCruce
      );

      if (partidoVuelta) {
        if (!partidoVuelta.equipoLocal || !partidoVuelta.equipoVisitante) {
          partidoVuelta = {
            ...partidoVuelta,
            equipoLocal: partido.equipoVisitante,
            equipoVisitante: partido.equipoLocal,
          };
        }

        const dtos = [DTOs.SavePartidoFixture(partido), DTOs.SavePartidoFixture(partidoVuelta)];
        const equiposIda = { local: partido.equipoLocal, visitante: partido.equipoVisitante };
        const vueltatieneSoloEquipos = !partidoVuelta.anioPartido;

        postData(`torneos/${torneo.id}/partidos`, dtos, () => {
          edicion.setPartidoGrabandose(null);
          edicion.limpiarErrorFila(partido.idPartido);
          setPartidosFaltantes((prev) =>
            prev.filter(
              (p) => p.idPartido !== partido.idPartido && p.idPartido !== partidoVuelta.idPartido
            )
          );
          const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
          fetchData(url, (partidos) => {
            const partidosMapeados = mapearPartidosConInstancia(partidos);
            setPartidosExistentes(partidosMapeados);

            if (vueltatieneSoloEquipos) {
              const partidoVueltaCreado = partidosMapeados.find(
                (p) =>
                  p.codigoInstancia === Instancia.VUELTA &&
                  p.equipoLocal?.id === equiposIda.visitante?.id &&
                  p.equipoVisitante?.id === equiposIda.local?.id &&
                  !p.anioPartido &&
                  !p.resultadoLocal &&
                  !p.resultadoVisitante
              );

              if (partidoVueltaCreado) {
                edicion.iniciarEdicion(partidoVueltaCreado);
              }
            }
          });
        });
        return;
      }
    }

    // Para partidos VUELTA o UNICO
    const dto = DTOs.SavePartidoFixture(partido);
    postData(`torneos/${torneo.id}/partidos`, [dto], () => {
      edicion.setPartidoGrabandose(null);
      setPartidosFaltantes((prev) => prev.filter((p) => p.idPartido !== partido.idPartido));
      recargarPartidos();
    });
  };

  const onCancelarPartido = (partido) => {
    const nuevoPartido = DTOs.PartidoFixtureVacio(
      datosElegidos,
      partido.idPartido,
      partido.codigoInstancia,
      partido.indiceCruce,
      partido.zona,
      partido.nroFecha
    );

    const indexEnFaltantes = partidosFaltantes.findIndex((p) => p.idPartido === partido.idPartido);
    if (indexEnFaltantes !== -1) {
      const nuevosPartidos = [...partidosFaltantes];
      nuevosPartidos[indexEnFaltantes] = nuevoPartido;
      setPartidosFaltantes(nuevosPartidos);
    }
  };

  const onEditarPartido = (idPartido) => {
    const partido = partidosExistentes.find((p) => p.id === idPartido);
    if (partido) {
      edicion.iniciarEdicion(partido);
    }
  };

  const onGuardarEdicion = async (partido) => {
    const errorBase = Funciones.ValidarPartidoFixture(partido, { requiereFecha: true });
    if (errorBase) {
      alert(errorBase);
      return;
    }

    if (partido.codigoInstancia === Instancia.IDA || partido.codigoInstancia === Instancia.UNICO) {
      const otrosExistentes = partidosExistentes.filter(
        (p) => p.id !== partido.id && p.codigoInstancia === partido.codigoInstancia
      );
      const otrosFaltantes = partidosFaltantes.filter(
        (p) => p.codigoInstancia === partido.codigoInstancia
      );
      const errorRepetidos = Funciones.ValidarEquiposRepetidos(
        [partido],
        [...otrosExistentes, ...otrosFaltantes],
        'instancia'
      );
      if (errorRepetidos) {
        alert(errorRepetidos);
        return;
      }
    }

    resetErroresPatch();
    edicion.setPartidoGrabandose(partido.id);

    // Si es un partido de IDA existente, verificar si se modificaron los equipos
    if (partido.codigoInstancia === Instancia.IDA && etapa.cantidadRuedas === 2) {
      const partidoOriginal = partidosExistentes.find((p) => p.id === partido.id);
      const equiposModificados =
        partidoOriginal &&
        (partidoOriginal.equipoLocal?.id !== partido.equipoLocal?.id ||
          partidoOriginal.equipoVisitante?.id !== partido.equipoVisitante?.id);

      if (equiposModificados) {
        const confirmar = window.confirm(
          'ATENCIÓN: Al modificar los equipos del partido de IDA, también se modificarán los equipos del partido de VUELTA correspondiente. ¿Desea continuar?'
        );
        if (!confirmar) {
          return;
        }

        let partidoVuelta = partidosExistentes.find(
          (p) =>
            p.codigoInstancia === Instancia.VUELTA &&
            p.ordenPartido &&
            partido.ordenPartido &&
            p.ordenPartido === partido.ordenPartido
        );

        if (!partidoVuelta) {
          partidoVuelta = partidosExistentes.find(
            (p) =>
              p.codigoInstancia === Instancia.VUELTA &&
              p.equipoLocal?.id === partidoOriginal.equipoVisitante?.id &&
              p.equipoVisitante?.id === partidoOriginal.equipoLocal?.id
          );
        }

        if (partidoVuelta) {
          const partidoVueltaBase = partidosEnEdicion[partidoVuelta.id] || partidoVuelta;

          const dtoIda = {
            idPartido: partido.id,
            ...DTOs.PatchPartidoFixture(partido),
          };
          const dtoVuelta = {
            idPartido: partidoVueltaBase.id,
            ...DTOs.PatchPartidoFixture({
              ...partidoVueltaBase,
              equipoLocal: partido.equipoVisitante,
              equipoVisitante: partido.equipoLocal,
            }),
          };

          patchData(`torneos/${torneo.id}/partidos`, [dtoIda, dtoVuelta], () => {
            edicion.setPartidoGrabandose(null);
            recargarPartidos();
            edicion.limpiarEdicionMultiple([partido.id, partidoVueltaBase.id]);
          });
          return;
        } else {
          console.warn(
            'No se encontró el partido de VUELTA correspondiente. Solo se actualizará el partido de IDA.',
            { partidoIda: partido.id, ordenPartido: partido.ordenPartido }
          );
          alert(
            'ADVERTENCIA: No se encontró el partido de VUELTA correspondiente. Solo se actualizará el partido de IDA. Verifique los datos.'
          );
        }
      }
    }

    // Para otros casos (sin modificación de equipos, o VUELTA, o UNICO)
    const dto = DTOs.PatchPartidoFixture(partido);
    patchData(`partidos/${partido.id}`, dto, () => {
      edicion.setPartidoGrabandose(null);
      recargarPartidos();
      edicion.cancelarEdicion(partido.id);
    });
  };

  const onEliminarPartido = (idPartido) => {
    edicion.eliminarPartido(idPartido, recargarPartidos);
  };

  const onGuardarTodos = async () => {
    let partidosNuevosValidos = partidosFaltantes.filter((p) => {
      return p.anioPartido && p.equipoLocal && p.equipoVisitante;
    });

    // Si hay etapa ida y vuelta, agregar partidos de VUELTA automáticamente cuando se crea IDA
    if (etapa.cantidadRuedas === 2) {
      const partidosIdaValidos = partidosNuevosValidos.filter(
        (p) => p.codigoInstancia === Instancia.IDA
      );

      partidosIdaValidos.forEach((partidoIda) => {
        const tieneVuelta = partidosNuevosValidos.some(
          (pv) =>
            pv.codigoInstancia === Instancia.VUELTA && pv.indiceCruce === partidoIda.indiceCruce
        );
        if (!tieneVuelta) {
          let partidoVuelta = partidosFaltantes.find(
            (p) =>
              p.codigoInstancia === Instancia.VUELTA && p.indiceCruce === partidoIda.indiceCruce
          );
          if (partidoVuelta) {
            partidoVuelta = {
              ...partidoVuelta,
              equipoLocal: partidoIda.equipoVisitante,
              equipoVisitante: partidoIda.equipoLocal,
            };
            partidosNuevosValidos.push(partidoVuelta);
          }
        }
      });
    }

    let partidosEditadosValidos = Object.values(partidosEnEdicion).filter((p) => {
      return p && p.id && p.anioPartido && p.equipoLocal && p.equipoVisitante;
    });

    const partidoConErrorBase = [...partidosNuevosValidos, ...partidosEditadosValidos].find((p) =>
      Funciones.ValidarPartidoFixture(p, { requiereFecha: true })
    );
    if (partidoConErrorBase) {
      alert(Funciones.ValidarPartidoFixture(partidoConErrorBase, { requiereFecha: true }));
      return;
    }

    const partidosIdaNuevos = partidosNuevosValidos.filter(
      (p) => p.codigoInstancia === Instancia.IDA
    );
    const partidosIdaEditados = partidosEditadosValidos.filter(
      (p) => p.codigoInstancia === Instancia.IDA
    );
    const partidosExistentesNoEditados = partidosExistentes.filter(
      (p) =>
        !partidosEnEdicion[p.id] &&
        (p.codigoInstancia === Instancia.IDA || p.codigoInstancia === Instancia.UNICO)
    );
    const errorIda = Funciones.ValidarEquiposRepetidos(
      [...partidosIdaNuevos, ...partidosIdaEditados],
      partidosExistentesNoEditados,
      'IDA'
    );
    if (errorIda) {
      alert(errorIda);
      return;
    }

    // Para partidos de IDA editados, verificar si se modificaron equipos y actualizar VUELTA
    if (etapa.cantidadRuedas === 2) {
      for (const partidoIda of partidosIdaEditados) {
        const partidoOriginal = partidosExistentes.find((p) => p.id === partidoIda.id);
        const equiposModificados =
          partidoOriginal &&
          (partidoOriginal.equipoLocal?.id !== partidoIda.equipoLocal?.id ||
            partidoOriginal.equipoVisitante?.id !== partidoIda.equipoVisitante?.id);

        if (equiposModificados) {
          let partidoVuelta = partidosExistentes.find(
            (p) =>
              p.codigoInstancia === Instancia.VUELTA &&
              p.ordenPartido &&
              partidoIda.ordenPartido &&
              p.ordenPartido === partidoIda.ordenPartido
          );

          if (!partidoVuelta) {
            partidoVuelta = partidosExistentes.find(
              (p) =>
                p.codigoInstancia === Instancia.VUELTA &&
                p.equipoLocal?.id === partidoOriginal.equipoVisitante?.id &&
                p.equipoVisitante?.id === partidoOriginal.equipoLocal?.id
            );
          }

          if (partidoVuelta && !partidosEditadosValidos.find((p) => p.id === partidoVuelta.id)) {
            const partidoVueltaBase = partidosEnEdicion[partidoVuelta.id] || partidoVuelta;
            partidosEditadosValidos.push({
              ...partidoVueltaBase,
              equipoLocal: partidoIda.equipoVisitante,
              equipoVisitante: partidoIda.equipoLocal,
            });
          } else if (!partidoVuelta) {
            console.warn('No se encontró el partido de VUELTA correspondiente al partido de IDA.', {
              partidoIda: partidoIda.id,
              ordenPartido: partidoIda.ordenPartido,
            });
          }
        }
      }
    }

    const totalParaGuardar = partidosNuevosValidos.length + partidosEditadosValidos.length;

    if (totalParaGuardar === 0) {
      alert('No hay partidos válidos para guardar');
      return;
    }

    const mensaje = [];
    if (partidosNuevosValidos.length > 0) {
      mensaje.push(`crear ${partidosNuevosValidos.length} partido(s)`);
    }
    if (partidosEditadosValidos.length > 0) {
      mensaje.push(`actualizar ${partidosEditadosValidos.length} partido(s)`);
    }

    const hayModificacionEquipos =
      etapa.cantidadRuedas === 2 &&
      partidosEditadosValidos.some((p) => {
        const original = partidosExistentes.find((po) => po.id === p.id);
        return (
          p.codigoInstancia === Instancia.IDA &&
          original &&
          (original.equipoLocal?.id !== p.equipoLocal?.id ||
            original.equipoVisitante?.id !== p.equipoVisitante?.id)
        );
      });

    let mensajeConfirmacion = `¿Está seguro que desea ${mensaje.join(' y ')}?`;
    if (hayModificacionEquipos) {
      mensajeConfirmacion =
        'ATENCIÓN: Se modificaron equipos en partido(s) de IDA. También se actualizarán los equipos en los partidos de VUELTA correspondientes.\n\n' +
        mensajeConfirmacion;
    }

    const confirmar = window.confirm(mensajeConfirmacion);
    if (!confirmar) {
      return;
    }

    resetErroresPatch();

    if (partidosNuevosValidos.length > 0) {
      const partidosVueltaSinFecha = partidosNuevosValidos
        .filter((p) => p.codigoInstancia === Instancia.VUELTA && !p.anioPartido)
        .map((p) => ({ localId: p.equipoLocal?.id, visitanteId: p.equipoVisitante?.id }));

      const dtosNuevos = partidosNuevosValidos.map((partido) => DTOs.SavePartidoFixture(partido));
      postData(`torneos/${torneo.id}/partidos`, dtosNuevos, () => {
        setPartidosFaltantes((prev) =>
          prev.filter((p) => !partidosNuevosValidos.some((pn) => pn.idPartido === p.idPartido))
        );
        const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
        fetchData(url, (partidos) => {
          const partidosMapeados = mapearPartidosConInstancia(partidos);
          setPartidosExistentes(partidosMapeados);

          if (partidosVueltaSinFecha.length > 0) {
            partidosVueltaSinFecha.forEach((vuelta) => {
              const partidoVueltaCreado = partidosMapeados.find(
                (p) =>
                  p.codigoInstancia === Instancia.VUELTA &&
                  p.equipoLocal?.id === vuelta.localId &&
                  p.equipoVisitante?.id === vuelta.visitanteId &&
                  !p.anioPartido &&
                  !p.resultadoLocal &&
                  !p.resultadoVisitante
              );

              if (partidoVueltaCreado) {
                edicion.iniciarEdicion(partidoVueltaCreado);
              }
            });
          }
        });
      });
    }

    if (partidosEditadosValidos.length > 0) {
      const dtosEditados = partidosEditadosValidos.map((partido) => ({
        idPartido: partido.id,
        ...DTOs.PatchPartidoFixture(partido),
      }));

      const idsPartidosEditados = partidosEditadosValidos.map((p) => p.id);

      patchData(`torneos/${torneo.id}/partidos`, dtosEditados, () => {
        idsPartidosEditados.forEach((id) => edicion.limpiarErrorFila(id));
        recargarPartidos();
        edicion.limpiarEdicionMultiple(idsPartidosEditados);
      });
    }
  };

  // Separar partidos por instancia para cada lista
  const partidosExistentesPorInstancia = Funciones.AgruparPartidosPorInstancia(partidosExistentes);
  const partidosFaltantesPorInstancia = Funciones.AgruparPartidosPorInstancia(partidosFaltantes);
  const cantidadRuedas = etapa?.cantidadRuedas || 1;

  const propsComunesTabla = (instancia) => ({
    partidosExistentes: Funciones.OrdenarPartidos(partidosExistentesPorInstancia[instancia] || []),
    partidosFaltantes: puedeEditar
      ? Funciones.OrdenarPartidos(partidosFaltantesPorInstancia[instancia] || [])
      : [],
    equipos: equiposDisponibles,
    torneo,
    mostrarZona: etapa?.zonas === true,
    puedeEditar,
    partidosEnEdicion,
    partidoGrabandose,
    erroresFilaPartido,
    onEditarPartido,
    onCancelarEdicion: edicion.cancelarEdicion,
    onPartidoEnEdicionChange: edicion.actualizarEdicion,
    onGuardarEdicion,
    onEliminarPartido,
    onPartidoChange,
    onGuardarPartido,
    onCancelarPartido,
  });

  // Si no es etapa de definición, no mostrar nada
  if (!etapa || etapa.definicion !== true) {
    return null;
  }

  return (
    <div className={styles.contenedor}>
      {cantidadRuedas === 1 && (
        <div className={styles.grupoPartidoUnico}>
          <TablaInstanciaFixture {...propsComunesTabla(Instancia.UNICO)} esVuelta={false} />
        </div>
      )}

      {cantidadRuedas === 2 && (
        <React.Fragment>
          <TablaInstanciaFixture
            titulo="Ida"
            {...propsComunesTabla(Instancia.IDA)}
            esVuelta={false}
          />
          <TablaInstanciaFixture
            titulo="Vuelta"
            tituloLocal="Local (auto)"
            tituloVisitante="Visitante (auto)"
            {...propsComunesTabla(Instancia.VUELTA)}
            esVuelta={true}
          />
        </React.Fragment>
      )}

      {puedeEditar &&
        (partidosFaltantes.length > 0 || Object.keys(partidosEnEdicion).length > 0) && (
          <div className={styles.acciones}>
            <button
              onClick={onGuardarTodos}
              disabled={isGrabando || isGrabandoPatch}
              className={styles.btnGuardarTodos}
            >
              Guardar todos los partidos editados
            </button>
            <MensajeGrabando
              isGrabando={isGrabando || isGrabandoPatch}
              entidad="partido"
              plural={true}
              estilo={styles.mensajeGrabandoTodos}
            />
          </div>
        )}

      <MensajeCargando isCargando={isCargando} />
      <MensajeError errores={erroresGetLista} />
      <MensajeError errores={erroresPost} />
      <MensajeError errores={erroresPatch} />
    </div>
  );
};

export default FixtureEtapaDefinicion;
